import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/ui/Header';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import apiRequest from "@/services/requests";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const JobPostDetails = () => {
const { id } = useParams();
const navigate = useNavigate();
const [jobMatch, setJobMatch] = useState(null);
const [isLoading, setIsLoading] = useState(true);
const [isRemoving, setIsRemoving] = useState(false);

useEffect(() => {
loadJobDetails();
}, [id]);

const loadJobDetails = async () => {
setIsLoading(true);
try {
const response = await apiRequest.getMatchingDetails(id);
setJobMatch(response);
} catch (error) {
console.error('Failed to load job details:', error);
toast.error('Could not load job details');
navigate('/job-matching');
} finally {
setIsLoading(false); 
}
};

const handleRemove = async () => {
setIsRemoving(true);
try {
await apiRequest.matchingDelete(id);
toast.success('Application submitted successfully!');
loadJobDetails();
} catch (error) {
console.error('Failed to apply:', error);
toast.error('Could not submit application');
} finally {
setIsRemoving(false);
}
};

const handleExternalApply = () => {
if (jobMatch?.application_url) {
window.open(jobMatch.application_url, '_blank');
}
};

const getMatchColor = (score) => {
if (score >= 80) return 'text-green-600 bg-green-50';
if (score >= 60) return 'text-yellow-600 bg-yellow-50';
return 'text-red-600 bg-red-50';
};


const formatSalary = (range, currency = 'USD') => {
if (!range) return 'Not specified';
const [min, max] = range.split('-').map(n => parseInt(n));
const format = (value) => {
if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
return `${value}`;
};
return `${currency} ${format(min)} - ${format(max)}`;
};


const formatDate = (date) => {
if (!date) return 'Not specified';
return new Date(date).toLocaleDateString('en-US', {
month: 'long',
day: 'numeric',
year: 'numeric'
});
};

if (isLoading) {
return (
<div className="min-h-screen bg-background">
<Header />
<main className="pt-16">
<div className="max-w-7xl mx-auto px-6 py-8">
<div className="flex items-center justify-center h-64">
<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
</div>
</div>
</main>
</div>
);
}

if (!jobMatch) return null;

const { match_score, match_breakdown, match_reasoning, matched_skills, missing_skills, applied, status, expires_at,remote_type,experience_level,application_url } = jobMatch;

return(
<div className="min-h-screen bg-background">
<Header />
<main className="pt-16">
<div className="max-w-7xl mx-auto px-6 py-8">


{/* Page Header */}
<div className="mb-8">
<div className="flex items-center justify-between mb-4">
<div className="flex items-center gap-4 flex-1">
<button
onClick={() => navigate('/job-matching')}
className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
>
<Icon name="ArrowLeft" size={20} />
</button>
<div className="flex-1">
<div className="flex items-center gap-3 mb-2">
{jobMatch?.company_logo_url ? (
<img
src={jobMatch.company_logo_url}
alt={jobMatch.company_name}
className="w-12 h-12 rounded-lg object-cover"
onError={(e) => {
e.target.style.display = 'none';
}}
/>
) : (
<div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
<Icon name="Briefcase" size={24} className="text-primary" />
</div>
)}
<div>
<h1 className="text-2xl font-bold text-text-primary">{jobMatch?.job_title}</h1>
<p className="text-text-secondary">{jobMatch?.company_name}</p>
</div>
</div>
<div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary">
<span className="flex items-center gap-1">
<Icon name="MapPin" size={16} />
{jobMatch?.location || 'Not specified'}
</span>
<span className="flex items-center capitalize gap-1">
<Icon name="Briefcase" size={16} />
{jobMatch?.employment_type || 'Not specified'}
</span>
<span className="flex items-center gap-1">
<Icon name="DollarSign" size={16} />
{formatSalary(jobMatch?.salary_range, jobMatch?.salary_currency)}
</span>

<span className="flex items-center gap-1">
<Icon name="Clock" size={16} />
{formatDate(jobMatch?.application_deadline)}
</span>

<span className="flex items-center capitalize gap-1">
<Icon name="Building" size={16} />
{jobMatch?.remote_type}
</span>

</div>
</div>
</div>
<div className="hidden md:flex items-center space-x-3">
{!applied ? (
<>
<Button
variant="danger"
iconName="Trash"
iconPosition="left"
iconSize={16}
onClick={handleRemove}
disabled={isRemoving}
>
{isRemoving ? 'Removing...' : 'Remove'}
</Button>
</>
) : (
<div className="px-4 py-2 bg-green-50 text-green-700 rounded-lg font-medium">
✓ Applied
</div>
)}
</div>
</div>
</div>
{/*End Page header*/}



{/* Match Score Section */}
<div className="mb-6">
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<div className="flex items-center justify-between mb-4">
<h2 className="text-lg font-semibold text-text-primary">AI Match Analysis</h2>
<div className={`px-4 py-2 rounded-full font-bold text-2xl ${getMatchColor(match_score)}`}>
{match_score}%
</div>
</div>
<p className="text-text-secondary mb-4">{match_reasoning}</p>

{/* Match Breakdown */}
<div className="grid grid-cols-2 md:grid-cols-5 gap-4">
{Object.entries(match_breakdown || {}).map(([key, value]) => (
<div key={key} className="text-center">
<div className="text-2xl font-bold text-primary">{value}%</div>
<div className="text-sm text-text-secondary capitalize">{key}</div>
</div>
))}
</div>
</div>
</div>
{/*End Match Score Section*/}



{/* Skills Match */}
<div className="grid md:grid-cols-2 gap-6 mb-6">
{/* Matched Skills */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
<Icon name="CheckCircle" size={20} className="text-green-600" />
Matched Skills
</h3>
<div className="flex flex-wrap gap-2">
{matched_skills?.map((skill, idx) => (
<span key={idx} className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
{skill}
</span>
))}
</div>
</div>

{/* Missing Skills */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
<Icon name="AlertCircle" size={20} className="text-orange-600" />
Skills to Learn
</h3>
<div className="flex flex-wrap gap-2">
{missing_skills?.length > 0 ? (
missing_skills.map((skill, idx) => (
<span key={idx} className="px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm font-medium">
{skill}
</span>
))
) : (
<p className="text-text-secondary text-sm">You have all required skills!</p>
)}
</div>
</div>
</div>
{/*End Skills Match*/}




{/* Job Details */}
<div className="grid md:grid-cols-3 gap-6">
{/* Main Content */}
<div className="md:col-span-2 space-y-6">
{/* Description */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-semibold text-text-primary mb-4">Job Description</h3>
<p className="text-text-secondary whitespace-pre-line">{jobMatch.job_description}</p>
</div>

{/* Responsibilities */}
{jobMatch.job_responsibilities && jobMatch.job_responsibilities.length > 0 && (
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-semibold text-text-primary mb-4">Responsibilities</h3>
<ul className="space-y-2">
{jobMatch.job_responsibilities.map((resp, idx) => (
<li key={idx} className="flex items-start gap-2 text-text-secondary">
<Icon name="Check" size={16} className="text-primary mt-1 flex-shrink-0" />
<span>{resp}</span>
</li>
))}
</ul>
</div>
)}

{/* Required Skills */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-semibold text-text-primary mb-4">Required Skills</h3>
<div className="flex flex-wrap gap-2 mb-4">
{jobMatch.required_skills?.map((skill, idx) => (
<span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
{skill}
</span>
))}
</div>
{jobMatch.preferred_skills && jobMatch.preferred_skills.length > 0 && (
<>
<h4 className="text-md font-semibold text-text-primary mb-3">Preferred Skills</h4>
<div className="flex flex-wrap gap-2">
{jobMatch.preferred_skills.map((skill, idx) => (
<span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
{skill}
</span>
))}
</div>
</>
)}
</div>

{/* Company Description */}
{jobMatch && (
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-semibold text-text-primary mb-4">About {jobMatch.company_name}</h3>
<p className="text-text-secondary mb-4">{jobMatch.description}</p>

{jobMatch.culture_values && jobMatch.culture_values.length > 0 && (
<>
<h4 className="text-md font-semibold text-text-primary mb-2">Company Values</h4>
<div className="flex flex-wrap gap-2 mb-4">
{jobMatch.culture_values.map((value, idx) => (
<span key={idx} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">
  {value}
</span>
))}
</div>
</>
)}

{jobMatch.tech_stack && jobMatch.tech_stack.length > 0 && (
<>
<h4 className="text-md font-semibold text-text-primary mb-2">Tech Stack</h4>
<div className="flex flex-wrap gap-2">
{jobMatch.tech_stack.map((tech, idx) => (
<span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
  {tech}
</span>
))}
</div>
</>
)}
</div>
)}
</div>

{/* Sidebar */}
<div className="space-y-6">
{/* Quick Facts */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-semibold text-text-primary mb-4">Quick Facts</h3>
<div className="space-y-3">
<div>
<div className="text-sm text-text-secondary">Experience Level</div>
<div className="font-medium text-text-primary capitalize">{jobMatch.experience_level}</div>
</div>
<div>
<div className="text-sm text-text-secondary">Years Required</div>
<div className="font-medium text-text-primary">{jobMatch.years_of_experience_required || 'Not specified'}</div>
</div>
<div>
<div className="text-sm text-text-secondary">Education</div>
<div className="font-medium text-text-primary">{jobMatch.education_requirement || 'Not specified'}</div>
</div>
<div>
<div className="text-sm text-text-secondary">Remote Type</div>
<div className="font-medium text-text-primary capitalize">{jobMatch.remote_type || 'Not specified'}</div>
</div>
<div>
<div className="text-sm text-text-secondary">Application Deadline</div>
<div className="font-medium text-text-primary">{formatDate(jobMatch.application_deadline)}</div>
</div>
</div>
</div>

{/* Benefits */}
{jobMatch.benefits && jobMatch.benefits.length > 0 && (
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-semibold text-text-primary mb-4">Benefits</h3>
<ul className="space-y-2">
{jobMatch.benefits.map((benefit, idx) => (
<li key={idx} className="flex items-center gap-2 text-text-secondary">
<Icon name="Check" size={16} className="text-green-600" />
<span>{benefit}</span>
</li>
))}
</ul>
</div>
)}

{/* Company Info */}
{jobMatch && (
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-semibold text-text-primary mb-4">Company Info</h3>
<div className="space-y-3">
<div>
<div className="text-sm text-text-secondary">Industry</div>
<div className="font-medium text-text-primary">{jobMatch.industry}</div>
</div>
<div>
<div className="text-sm text-text-secondary">Size</div>
<div className="font-medium text-text-primary">{jobMatch.employee_count} employees</div>
</div>
<div>
<div className="text-sm text-text-secondary">Founded</div>
<div className="font-medium text-text-primary">{jobMatch.founded_year}</div>
</div>
<div>
<div className="text-sm text-text-secondary">Location</div>
<div className="font-medium text-text-primary">{jobMatch.headquarters}</div>
</div>
{jobMatch.glassdoor_rating && (
<div>
<div className="text-sm text-text-secondary">Glassdoor Rating</div>
<div className="font-medium text-text-primary">⭐ {jobMatch.glassdoor_rating}/5</div>
</div>
)}
</div>
{jobMatch.website && (
<a
href={jobMatch.website}
target="_blank"
rel="noopener noreferrer"
className="mt-4 flex items-center gap-2 text-primary hover:underline"
>
<Icon name="ExternalLink" size={16} />
<span>Visit Website</span>
</a>
)}
</div>
)}
</div>
</div>

{/*End Job Details*/}




{/* Mobile Apply Buttons */}
<div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 space-y-2">
{!applied ? (
<>
<Button
variant="default"
iconName="Send"
iconPosition="left"
iconSize={16}
onClick={handleRemove}
disabled={isRemoving}
className="w-full"
>
{isRemoving ? 'Removing...' : 'Remove'}
</Button>
</>
) : (
<div className="w-full text-center px-4 py-3 bg-green-50 text-green-700 rounded-lg font-medium">
✓ Application Submitted
</div>
)}
</div>
{/*End Mobile Button*/}



</div>
</main>
</div>
)
};

export default JobPostDetails;